import { collectPageViewStat } from './google_analytics.js';
import { log, assert, error } from './logger.js';
import { navigation } from './navigation.js';

// depends on window

/**
 * Manages the browser history.
 */
class PageHistory {
  constructor() {
    window.onpopstate = ev => this.onpopstate(ev);
    this.origState = window.history.state;
  }

  get restorableState() {
    return (!this.stateChanged && this.origState) || null;
  }
  restoreState() {
    // Switch to the active panel if state wasn't change since page load.
    let state = this.restorableState;
    if (state) {
      log(`[history] restore state to '${state.page}' page`);
      this.onmoved(state);
      return true;
    }
    return false;
  }

  get state() {
    return window.history.state;
  }
  get stateParams() {
    return this.state && this.state.params;
  }
  get stateChanged() {
    return this._stateChanged || this.origState != window.history.state;
  }
  setStateChanged() {
    this._stateChanged = true;
  }

  isPage(page) {
    return window.history.state && window.history.state.page == page;
  }

  toPage(page) {
    log(`[history] moving to '${page}' page`);

    // No state case only if the page is just loaded. Replace history to set up
    // state to the page.
    let state = window.history.state;
    if (!state) {
      this.replacePage(page);
      return null;
    }

    if (state.page == page) {
      return this.replacePage(page);
    }

    // Pop history back if requested panel is a previous panel of the current
    // one, i.e. panel1 -> panel2 -> panel1 case.
    if (window.history.state.prevPage == page) {
      return this.fakeBack();
    }

    // Otherwise, push the page on the stack.
    let stateArgs = this.pageDriver(page).historyState;
    if (!stateArgs) {
      error(`[history] unexpected '${page}' page`);
      return null;
    }

    log(
      `[history] pushed '${page}' page on stack, params: ${JSON.stringify(
        state.params
      )}, prev page: ${window.history.state.page}`
    );
    stateArgs[0].prevPage = window.history.state.page;
    return Promise.resolve(window.history.pushState(...stateArgs)).then(
      collectPageViewStat
    );
  }

  replacePage(page) {
    let stateArgs = this.pageDriver(page).historyState;
    if (!stateArgs) {
      error(`[history] unexpected '${page}' history page`);
      return null;
    }

    let thisPage = (window.history.state && window.history.state.page) || '';
    let prevPage =
      (window.history.state && window.history.state.prevPage) || '';

    log(`[history] replace ${thisPage} page to '${page}' page`);
    stateArgs[0].prevPage = prevPage;
    return Promise.resolve(window.history.replaceState(...stateArgs)).then(
      collectPageViewStat
    );
  }

  back() {
    return this.state && this.state.prevPage && window.history.back();
  }

  onmoved(state) {
    if (!state) {
      error(`[history] no state on history popstate`);
      return;
    }

    let page = state.page;
    log(`[history] move to '${page}' page`);
    collectPageViewStat();

    let driver = this.pageDriver(page);
    navigation.toPanel(driver.page, state.params, 'no-history');
  }

  fakeBack() {
    let p = new Promise(resolve => {
      window.onpopstate = ev => {
        log(`[history] popped to ${ev.state && ev.state.page}, no processing`);
        collectPageViewStat();
        window.onpopstate = ev => this.onpopstate(ev);
        resolve();
      };
    });
    log(`[history] back from state ${JSON.stringify(this.state)}`);
    window.history.back();
    return p;
  }

  onpopstate(ev) {
    log(`[history] popped to ${ev.state && ev.state.page}`);
    this.onmoved(ev.state);
  }

  pageDriver(page) {
    let driver = navigation.findPanel(page);
    assert(driver, `[history] no page driver for '${page}' page`);
    return driver;
  }
}

export { PageHistory };
